<script lang="ts" setup>
import { AppButton, AppFormCard, AppTypography } from '@/components/base';
import CONFIG from '@/utils/Config';

const props = withDefaults(
  defineProps<{
    id: number | string;
    title: string;
    description: string;
    days_time?: any[];
    buttonText?: string;
    chatButtonText?: string;
    price?: string | number | null;
    isFullyVerified?: boolean;
    address?: {
      line1?: string;
      line2?: string;
      postcode?: string;
      city?: string;
      country?: string;
    };
    isAssigned?: boolean;
    isAccepted?: boolean;
    maxPayrate?: number;
    minPayrate?: number;
    isApplied?: boolean;
  }>(),
  {
    days_time: [],
    buttonText: 'View Job',
    chatButtonText: 'Chat',
    isAssigned: false,
    isAccepted: false,
    address: () => ({}),
    maxPayrate: 0,
    minPayrate: 0,
    // price: 0,
  }
);

const emits = defineEmits<{
  (e: 'view'): void;
}>();

const chat = (): void => {
  emits('chat');
};

const getDayName = (dayNumber: string): string => {
  const dayMap: { [key: string]: string } = {
    '1': 'Monday',
    '2': 'Tuesday', 
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday'
  };
  return dayMap[dayNumber] || `Day ${dayNumber}`;
};

const formatDaysAndTimes = (daysTime: any[]): string[] => {
  if (!Array.isArray(daysTime)) return [];
  
  return daysTime.map(day => {
    const dayNumber = Object.keys(day).find(key => key !== 'start_time' && key !== 'end_time');
    const dayName = getDayName(dayNumber || '');
    return `${dayName}    ${day.start_time} - ${day.end_time}`;
  });
};

const price = computed((): string => {
  return `$${props.price || 0} ${CONFIG.currency.label}`;
});

const view = (): void => {
  emits('view');
};

const showVerificationPrompt = ref(false);

function handleObscuredClick() {
  if (!props.isFullyVerified) {
    showVerificationPrompt.value = true;
  }
}

const formattedAddress = computed(() => {
  if (!props.address) return '';
  const { line1, line2, postcode, city, country } = props.address;
  return [
    line1,
    line2,
    [city, postcode].filter(Boolean).join(' '),
    country
  ].filter(Boolean).join(', ');
});
</script>
<template>
  <AppFormCard :no-shadow="false" :no-gutter="true" class="job-listing-card">
    <div class="job-listing-card__border text-left mb-4" @click="handleObscuredClick" style="cursor: pointer;">
      <div class="mb-3">
        <div class="title-chip-container">
          <AppTypography type="title-3" element="h3" :class="{ obscured: !isFullyVerified }">{{ title }}</AppTypography>
          <!-- Status Chip -->
          <div class="status-chip-container">
            <v-chip
              v-if="isAssigned"
              color="primary"
              size="small"
              class="status-chip"
            >
              Assigned
            </v-chip>
            <v-chip
              v-else-if="isAccepted"
              color="success"
              size="small"
              class="status-chip"
            >
              Accepted
            </v-chip>
          </div>
        </div>
      </div>
      <div class="mb-3">
        <AppTypography type="headline-1" class="job-listing-card__border__title mb-2" :class="{ obscured: !isFullyVerified }">Days/Hours :</AppTypography>
        <div v-if="days_time && Array.isArray(days_time)" :class="{ obscured: !isFullyVerified }">
          <div v-for="(dayTime, index) in formatDaysAndTimes(days_time)" :key="index" class="day-time-item">
            {{ dayTime }}
          </div>
        </div>
        <div v-else :class="{ obscured: !isFullyVerified }">
          <AppTypography type="body-1">
            Start Date: {{ days_time.start_date || '--' }}
          </AppTypography>
          <AppTypography type="body-1">
            End Date: {{ days_time.end_date || '--' }}
          </AppTypography> 
        </div>
      </div>
      <div class="mb-3">
        <AppTypography type="title-3" element="h3" :class="{ obscured: !isFullyVerified }">Description :</AppTypography>
        <AppTypography type="body-1" :class="{ obscured: !isFullyVerified }">{{ description }}</AppTypography>
      </div>
    </div>

    <div class="d-flex justify-space-between align-start">
      <div class="text-left">
        <AppTypography type="subheadline-1" class="mb-1">$ {{ minPayrate }} - $ {{ maxPayrate }} AUD</AppTypography>
        <AppTypography type="footnote-2">per hour</AppTypography>
      </div>
      <div class="d-flex align-center">
        <AppButton class="mr-2" size="small" v-if="isAccepted" @click="chat">{{ chatButtonText }}</AppButton>
        <AppButton size="small" @click="view">{{ buttonText }}</AppButton>
      </div>
    </div>
  </AppFormCard>
  <div v-if="showVerificationPrompt" class="modal-overlay" @click.self="showVerificationPrompt = false">
    <div class="modal-content">
      <span class="modal-close" @click="showVerificationPrompt = false" aria-label="Close">&times;</span>
      <div class="modal-message">
        Please complete your profile to view this feature.
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '@/assets/scss/_mixins.scss';

.job-listing-card {
  width: 100%;
  height: 100%;

  &__border {
    padding: 20px;
    background: #f3f6ff;
    border-radius: 12px;
    flex-grow: 1;

    &__title {
      @include mixins.wrap-text-ellipsis(1);
    }

    &__desc {
      @include mixins.wrap-text-ellipsis(2);
    }
  }

  :deep(.v-card-text) {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.obscured {
  filter: blur(4px) grayscale(1);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: modal-fade-in 0.2s;
}

@keyframes modal-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  background: #fff;
  padding: 2.5rem 2rem 2rem 2rem;
  border-radius: 16px;
  min-width: 340px;
  max-width: 604px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 1.5px 6px rgba(0,0,0,0.08);
  text-align: center;
  animation: modal-content-pop 0.2s;
}

@keyframes modal-content-pop {
  from { transform: scale(0.95); opacity: 0.7; }
  to { transform: scale(1); opacity: 1; }
}

.modal-close {
  position: absolute;
  top: 6px;
  right: 18px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #444;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s, transform 0.1s;
  z-index: 10000;
  line-height: 1;
}
.modal-close:hover {
  color: #e53935;
  transform: scale(1.15);
}

.modal-message {
  font-size: 16px;
  font-weight: 500;
  color: #222;
  letter-spacing: 0.01em;
}

.status-chip-container {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.title-chip-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.status-chip {
  font-weight: 500;
  font-size: 12px;
  flex-shrink: 0;
}

</style>
